/**
 * Queue-first portal landing-page response contracts.
 *
 * Every authenticated user has a single landing page determined by their
 * primary role. Each role's landing is served by its own endpoint under
 * `/api/landings/*` and returns the schema declared here. The portal
 * imports these types directly so the contract is shared end-to-end.
 *
 * Feature-flag gated fields use `.nullable()`: when a flag is OFF the field
 * is returned as `null`, and the frontend treats null as "feature not yet
 * available". When the module ships, the future agent flips the field from
 * `null` to a populated object — the schema stays compatible.
 *
 * All monetary amounts are integers in paise (1 INR = 100 paise) to match
 * the `fee_payments.amount_paise` convention. All timestamps are ISO 8601
 * UTC strings (the D1 `strftime` default produces `Z`-suffixed values).
 */
import { z } from 'zod';
import { isoDateTimeSchema, dateStringSchema } from './common.js';
import { roleSchema } from './auth.js';

// --- shared sub-schemas -----------------------------------------------------

const paiseAmountSchema = z.number().int().min(0);

const moduleHealthEntrySchema = z.object({
  enabled: z.boolean(),
  healthHint: z.string().nullable(),
});

const featureFlagItemSchema = z.object({
  key: z.string(),
  enabled: z.boolean(),
  description: z.string().nullable(),
});

const auditEventSchema = z.object({
  id: z.string(),
  action: z.string(),
  actorName: z.string().nullable(),
  actorRole: z.string().nullable(),
  targetType: z.string().nullable(),
  targetId: z.string().nullable(),
  occurredAt: isoDateTimeSchema,
});

const automationRunSchema = z.object({
  task: z.string(),
  status: z.enum(['ok', 'error', 'skipped']),
  ranAt: isoDateTimeSchema,
  notes: z.string().nullable(),
});

const announcementSummarySchema = z.object({
  id: z.string(),
  title: z.string(),
  publishedAt: isoDateTimeSchema.nullable(),
});

const batchSummarySchema = z.object({
  batchId: z.string(),
  batchName: z.string(),
  courseName: z.string().nullable(),
  attendanceRatePercent: z.number().int().min(0).max(100),
  lastWeeklySheetStatus: z
    .enum(['draft', 'submitted', 'approved', 'changes_requested', 'none'])
    .nullable(),
});

const weeklySheetQueueItemSchema = z.object({
  id: z.string(),
  batchId: z.string(),
  batchName: z.string(),
  teacherName: z.string().nullable(),
  weekStartDate: dateStringSchema,
  submittedAt: isoDateTimeSchema.nullable(),
});

const classSessionSummarySchema = z.object({
  id: z.string(),
  batchId: z.string(),
  batchName: z.string(),
  courseName: z.string().nullable(),
  scheduledAt: isoDateTimeSchema,
  durationMinutes: z.number().int().positive(),
  status: z.enum(['scheduled', 'in_progress', 'completed', 'cancelled']),
  lessonTitle: z.string().nullable(),
  attendanceMarked: z.boolean(),
});

// --- super_admin / support_viewer ------------------------------------------

export const controlCenterPayloadSchema = z.object({
  systemStatus: z.enum(['healthy', 'degraded', 'down']),
  activeUsers24h: z.number().int().min(0),
  pendingApprovalsTotal: z.number().int().min(0),
  featureFlags: z.object({
    enabled: z.number().int().min(0),
    disabled: z.number().int().min(0),
    items: z.array(featureFlagItemSchema),
  }),
  moduleStatus: z.record(z.string(), moduleHealthEntrySchema),
  recentAuditEvents: z.array(auditEventSchema).max(10),
  automationStatusToday: z.array(automationRunSchema).nullable(),
});

export const controlCenterResponseSchema = z.object({
  role: z.literal('super_admin'),
  data: controlCenterPayloadSchema,
});
export type ControlCenterPayload = z.infer<typeof controlCenterPayloadSchema>;
export type ControlCenterResponse = z.infer<typeof controlCenterResponseSchema>;

export const supportViewerResponseSchema = z.object({
  role: z.literal('support_viewer'),
  data: controlCenterPayloadSchema.extend({
    // Same shape as super_admin, but the action-required total is suppressed
    // to make the read-only nature visible to UI. The same number is still
    // computable; UI just shouldn't render any "go fix this" affordances.
    pendingApprovalsTotal: z.number().int().min(0).nullable(),
  }),
});
export type SupportViewerResponse = z.infer<typeof supportViewerResponseSchema>;

// --- academic_admin --------------------------------------------------------

export const operationsPayloadSchema = z.object({
  newApplicationsCount: z.number().int().min(0).nullable(),
  attendanceExceptionsToday: z.number().int().min(0),
  weeklySheetsToReview: z.number().int().min(0),
  atRiskBatchesCount: z.number().int().min(0),
  hiringPipelineDigest: z
    .array(
      z.object({
        candidateId: z.string(),
        name: z.string(),
        stage: z.string(),
        lastEventAt: isoDateTimeSchema,
      }),
    )
    .nullable(),
  recentAnnouncements: z.array(announcementSummarySchema).max(3),
  teacherPerformanceHighlights: z
    .object({
      top: z.array(
        z.object({
          teacherId: z.string(),
          name: z.string(),
          score: z.number(),
          band: z.string(),
        }),
      ),
      bottom: z.array(
        z.object({
          teacherId: z.string(),
          name: z.string(),
          score: z.number(),
          band: z.string(),
        }),
      ),
    })
    .nullable(),
});

export const operationsResponseSchema = z.object({
  role: z.literal('academic_admin'),
  data: operationsPayloadSchema,
});
export type OperationsPayload = z.infer<typeof operationsPayloadSchema>;
export type OperationsResponse = z.infer<typeof operationsResponseSchema>;

// --- finance_admin ---------------------------------------------------------

export const financeOpsPayloadSchema = z.object({
  overdueSummary: z.object({
    count: z.number().int().min(0),
    totalPaise: paiseAmountSchema,
  }),
  pendingClaims: z.number().int().min(0),
  withdrawalQueue: z
    .object({
      count: z.number().int().min(0),
      items: z.array(
        z.object({
          id: z.string(),
          requestedBy: z.string(),
          amountPaise: paiseAmountSchema,
          requestedAt: isoDateTimeSchema,
        }),
      ),
    })
    .nullable(),
  salaryCycleStatus: z
    .object({
      cycleId: z.string(),
      year: z.number().int(),
      month: z.number().int().min(1).max(12),
      payoutDate: dateStringSchema,
      unpaidCount: z.number().int().min(0),
    })
    .nullable(),
  recentActivity: z.array(
    z.object({
      id: z.string(),
      kind: z.enum(['fee_payment', 'salary_payment']),
      description: z.string(),
      amountPaise: paiseAmountSchema,
      occurredAt: isoDateTimeSchema,
    }),
  ),
});

export const financeOpsResponseSchema = z.object({
  role: z.literal('finance_admin'),
  data: financeOpsPayloadSchema,
});
export type FinanceOpsPayload = z.infer<typeof financeOpsPayloadSchema>;
export type FinanceOpsResponse = z.infer<typeof financeOpsResponseSchema>;

// --- nalim -----------------------------------------------------------------

export const academicOpsPayloadSchema = z.object({
  weeklySheetReviewQueue: z.object({
    count: z.number().int().min(0),
    items: z.array(weeklySheetQueueItemSchema),
  }),
  attendanceExceptionsToday: z.number().int().min(0),
  batchStatusCards: z.array(batchSummarySchema),
});

export const academicOpsResponseSchema = z.object({
  role: z.literal('nalim'),
  data: academicOpsPayloadSchema,
});
export type AcademicOpsPayload = z.infer<typeof academicOpsPayloadSchema>;
export type AcademicOpsResponse = z.infer<typeof academicOpsResponseSchema>;

// --- teacher ---------------------------------------------------------------

export const teacherTodayPayloadSchema = z.object({
  classesToday: z.array(classSessionSummarySchema),
  attendancePendingCount: z.number().int().min(0),
  tmaScore: z
    .object({
      score: z.number(),
      band: z.string(),
      period: z.string(),
    })
    .nullable(),
  walletBalance: z
    .object({
      academicYearPaise: paiseAmountSchema,
      confirmedReferralsCount: z.number().int().min(0),
    })
    .nullable(),
  thisWeeksQuiz: z
    .object({
      quizId: z.string(),
      title: z.string(),
      publishedAt: isoDateTimeSchema,
      participantsCount: z.number().int().min(0),
      averageScore: z.number().nullable(),
    })
    .nullable(),
  parentFeedbackAggregate: z
    .union([
      z.object({
        insufficient: z.literal(true),
        responsesCount: z.number().int().min(0),
      }),
      z.object({
        insufficient: z.literal(false),
        responsesCount: z.number().int().min(5),
        averageRating: z.number(),
        period: z.string(),
      }),
    ])
    .nullable(),
  weeklySheets: z.object({
    pendingSubmission: z.number().int().min(0),
    approved: z.number().int().min(0),
    revisionRequested: z.number().int().min(0),
  }),
});

export const teacherTodayResponseSchema = z.object({
  role: z.literal('teacher'),
  data: teacherTodayPayloadSchema,
});
export type TeacherTodayPayload = z.infer<typeof teacherTodayPayloadSchema>;
export type TeacherTodayResponse = z.infer<typeof teacherTodayResponseSchema>;

// --- parent ----------------------------------------------------------------

const familyChildSchema = z.object({
  studentId: z.string(),
  name: z.string(),
  batchName: z.string().nullable(),
  attendancePercent30d: z.number().int().min(0).max(100),
  latestRemark: z
    .object({
      note: z.string(),
      occurredAt: isoDateTimeSchema,
    })
    .nullable(),
});

export const familyPayloadSchema = z.object({
  children: z.array(familyChildSchema),
  feeDueSummary: z.object({
    amountPaise: paiseAmountSchema,
    dueDate: dateStringSchema.nullable(),
    paymentIds: z.array(z.string()),
  }),
  pendingFeedbackCount: z.number().int().min(0).nullable(),
  walletBalance: z
    .object({
      totalPaise: paiseAmountSchema,
      perChild: z.array(
        z.object({
          studentId: z.string(),
          balancePaise: paiseAmountSchema,
        }),
      ),
    })
    .nullable(),
  recentMarks: z
    .array(
      z.object({
        studentId: z.string(),
        studentName: z.string(),
        subject: z.string(),
        score: z.number(),
        maxScore: z.number(),
        recordedAt: isoDateTimeSchema,
      }),
    )
    .nullable(),
  referralCode: z
    .array(
      z.object({
        studentId: z.string(),
        code: z.string(),
      }),
    )
    .nullable(),
});

export const familyResponseSchema = z.object({
  role: z.literal('parent'),
  data: familyPayloadSchema,
});
export type FamilyPayload = z.infer<typeof familyPayloadSchema>;
export type FamilyResponse = z.infer<typeof familyResponseSchema>;

// --- student ---------------------------------------------------------------

export const studentProgressPayloadSchema = z.object({
  attendancePercent30d: z.number().int().min(0).max(100),
  recentMarks: z
    .array(
      z.object({
        subject: z.string(),
        score: z.number(),
        maxScore: z.number(),
        recordedAt: isoDateTimeSchema,
      }),
    )
    .nullable(),
  quizStreak: z
    .object({
      currentStreakWeeks: z.number().int().min(0),
      lastQuizScore: z.number().nullable(),
    })
    .nullable(),
  walletBalance: z
    .object({
      balancePaise: paiseAmountSchema,
    })
    .nullable(),
  recentTeacherRemark: z
    .object({
      note: z.string(),
      occurredAt: isoDateTimeSchema,
    })
    .nullable(),
  upcomingClasses: z.array(
    z.object({
      id: z.string(),
      batchName: z.string(),
      scheduledAt: isoDateTimeSchema,
      durationMinutes: z.number().int().positive(),
      lessonTitle: z.string().nullable(),
    }),
  ),
});

export const studentProgressResponseSchema = z.object({
  role: z.literal('student'),
  data: studentProgressPayloadSchema,
});
export type StudentProgressPayload = z.infer<typeof studentProgressPayloadSchema>;
export type StudentProgressResponse = z.infer<typeof studentProgressResponseSchema>;

// --- discriminated union (convenience for the portal) ----------------------

export const landingResponseSchema = z.discriminatedUnion('role', [
  controlCenterResponseSchema,
  supportViewerResponseSchema,
  operationsResponseSchema,
  financeOpsResponseSchema,
  academicOpsResponseSchema,
  teacherTodayResponseSchema,
  familyResponseSchema,
  studentProgressResponseSchema,
]);
export type LandingResponse = z.infer<typeof landingResponseSchema>;

/**
 * The canonical list of module feature-flag keys. Keep in sync with the
 * seed migration `0001_phase0_roles_and_feature_flags.sql`.
 */
export const moduleFlagKeys = [
  'module_exams',
  'module_assessment_engine',
  'module_wallet_referrals',
  'module_games',
  'module_hiring',
  'module_feedback',
  'module_tma',
  'module_teacher_reports',
] as const;
export type ModuleFlagKey = (typeof moduleFlagKeys)[number];

/**
 * Roles that have a landing page contract. Mirrors `roleSchema` minus
 * roles that don't surface a landing yet (e.g. `candidate`, `hr` — those
 * land in role-specific module pages once Hiring ships).
 */
export const landingRoleSchema = roleSchema.extract([
  'super_admin',
  'academic_admin',
  'finance_admin',
  'nalim',
  'teacher',
  'parent',
  'student',
  'support_viewer',
]);
export type LandingRole = z.infer<typeof landingRoleSchema>;
